import React, { useState } from 'react';
import './PriceComparison.css';
import { getCategoryGradient } from '../utils/db';
import { getSubcategoryDisplay } from '../utils/taxonomy';
import { buildComparisonSummary } from '../utils/comparison';
import { trackAddToList, trackProductView } from '../utils/analytics';
import FlagPriceSheet from './FlagPriceSheet';
import RequestModal from './requests/RequestModal';
import Toast from './Toast';

// ── DEV: local icon overrides for testing before Firestore iconUrl is populated ──
function getIconOverride(product) {
  const name = (product.name || '').toLowerCase();
  if (name.includes('milk'))                                return '/product-icons/milk.png';
  if (name.includes('grated') && name.includes('cheese'))   return '/product-icons/grated-cheese.png';
  if (name.includes('sliced') && name.includes('cheese'))   return '/product-icons/sliced-cheese.png';
  if (name.includes('cheese'))                              return '/product-icons/block-cheese.png';
  if (name.includes('olive oil'))                           return '/product-icons/olive-oil.png';
  if (name.includes('spaghetti'))                           return '/product-icons/spaghetti.png';
  if (name.includes('egg'))                                 return '/product-icons/eggs.png';
  if (name.includes('baked bean'))                          return '/product-icons/baked-beans.png';
  if (name.includes('unsalted') && name.includes('butter')) return '/product-icons/unsalted-butter.png';
  if (name.includes('butter'))                              return '/product-icons/salted-butter.png';
  if (name.includes('brown') && name.includes('bread'))     return '/product-icons/brown-bread.png';
  if (name.includes('seeded') && name.includes('bread'))    return '/product-icons/seeded-bread.png';
  if (name.includes('white') && name.includes('bread'))     return '/product-icons/white-bread.png';
  if (name.includes('banana'))                              return '/product-icons/bananas.png';
  return null;
}

function isTallIcon(product) {
  const name = (product?.name || '').toLowerCase();
  return name.includes('milk') || name.includes('olive oil') || name.includes('spaghetti');
}

function getConfidenceLabel(product) {
  if (!product) return 'Recently checked';
  if (product.confidence === 'seen_in_store') return 'Seen in store recently';
  if (product.confidence === 'old') return 'Last checked a while ago';
  return 'Recently checked';
}

// ── Tier ordering + labels (data-driven — only listed tiers are shown) ────────
const TIER_ORDER  = ['basic', 'standard', 'premium', 'organic'];
const TIER_LABELS = { basic: 'Basic', standard: 'Standard', premium: 'Premium', organic: 'Organic' };

// ── Format raw variant slugs for display ─────────────────────────────────────
// e.g. 'extra_mature' → 'Extra Mature', 'semi_skimmed' → 'Semi Skimmed'
const formatVariant = (v) =>
  (v || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

// ── Format size from standardizedSize + unitType fields ──────────────────────
// Product docs store size as `standardizedSize` (plain number) + `unitType`.
// e.g. ('500', 'g') → '500g'  |  ('1', 'l') → '1L'  |  ('6', 'each') → '6 pack'
const formatSize = (standardizedSize, unitType) => {
  if (!standardizedSize) return null;
  const unit = (unitType || '').toLowerCase().trim();
  if (unit === 'l')    return `${standardizedSize}L`;
  if (unit === 'each') return `${standardizedSize} pack`;
  if (unit)            return `${standardizedSize}${unit}`;
  return `${standardizedSize}`;
};

// ── Per-unit price helper ─────────────────────────────────────────────────────
// Returns pounds per normalised unit (100g / 100ml / item) so stores selling
// different sizes can be fairly compared. p.price is stored in pounds.
function calcUnitPrice(pricePounds, size, unitType) {
  const sz   = parseFloat(size);
  const unit = (unitType || '').toLowerCase().trim();
  if (!sz || sz <= 0) return null;
  if (unit === 'g')    return { up: (pricePounds / sz) * 100,          label: '100g'  };
  if (unit === 'kg')   return { up: (pricePounds / (sz * 1000)) * 100, label: '100g'  };
  if (unit === 'ml')   return { up: (pricePounds / sz) * 100,          label: '100ml' };
  if (unit === 'l')    return { up: (pricePounds / (sz * 1000)) * 100, label: '100ml' };
  if (unit === 'each') return { up: pricePounds / sz,                  label: 'item'  };
  return null;
}

// up is in pounds per unit (e.g. 0.19 = 19p/100g) — convert to pence for display
function formatUnitPrice(pounds) {
  const pence = pounds * 100;
  if (pence < 100) return `${Number.isInteger(Math.round(pence * 10) / 10) ? Math.round(pence) : pence.toFixed(1)}p`;
  return `£${pounds.toFixed(2)}`;
}

// ── Build store-level rows from a set of siblings ─────────────────────────────
// Groups all prices by store, finds cheapest per store, detects multiple variants.
// Returns rows sorted cheapest first.
//
// sizeFilter (optional): when set, only price entries whose retailer-level size
// matches are included. This handles BOTH data patterns:
//   • Different sizes as separate product docs (eggs 6-pack / 12-pack): prices on
//     different siblings, each sibling's retailer docs share the same size.
//   • Different sizes as retailer docs under one product doc (olive oil 250ml / 500ml):
//     one sibling, retailer docs each have a distinct storeSize value.
function computeStoreRows(siblings, sizeFilter = null) {
  const allPrices = siblings.flatMap(s =>
    (s.prices || [])
      .filter(p => p.price > 0)
      .filter(p => {
        if (!sizeFilter) return true;
        return formatSize(p.size, p.unitType || s.unitType) === sizeFilter;
      })
      .map(p => ({
        ...p,
        siblingId:    s.id,
        siblingTier:  s.tier,
        siblingName:  s.name  || null,
        siblingBrand: s.brand || null,
        // Actual store size from the retailer doc (p.size = storeSize || standardizedSize).
        // unitType falls back to the product doc's unitType if not on the retailer doc.
        siblingSize:    formatSize(p.size, p.unitType || s.unitType) || null,
        unitPriceData:  calcUnitPrice(p.price, p.size, p.unitType || s.unitType),
      }))
  );

  const byStore = {};
  allPrices.forEach(p => {
    if (!byStore[p.storeId]) byStore[p.storeId] = [];
    byStore[p.storeId].push(p);
  });

  return Object.entries(byStore)
    .map(([storeId, prices]) => {
      const sorted = [...prices].sort((a, b) => a.price - b.price);
      const distinctSizes = new Set(sorted.map(p => p.siblingSize).filter(Boolean));
      const unitPriceOptions = sorted.map(v => v.unitPriceData).filter(Boolean);
      return {
        storeId,
        cheapestPrice:     sorted[0].price,
        cheapestEntry:     sorted[0],
        variants:          sorted,
        hasVariants:       sorted.length > 1,
        hasMixedSizes:     distinctSizes.size > 1,
        cheapestUnitPrice: unitPriceOptions.length
          ? unitPriceOptions.reduce((a, b) => a.up < b.up ? a : b)
          : null,
      };
    })
    .sort((a, b) => a.cheapestPrice - b.cheapestPrice);
}

// ─────────────────────────────────────────────────────────────────────────────

function PriceComparison({ product, prices: pricesProp, siblings = [], stores, onAddToBasket, onBack, aisleContext, onBackToAisle }) {

  // ── Track product view on mount ───────────────────────────────────────────
  React.useEffect(() => {
    if (product?.name) trackProductView(product.name, product.category);
  }, [product?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── UI state ─────────────────────────────────────────────────────────────
  const [expandedStores,    setExpandedStores]    = useState(new Set());
  const [flagOpen,          setFlagOpen]          = useState(false);
  const [requestModalOpen,  setRequestModalOpen]  = useState(false);
  const [showRequestToast,  setShowRequestToast]  = useState(false);

  // ── Comparison mode state ─────────────────────────────────────────────────
  // compareMode: 'any' | 'own_brand' | 'branded' | 'brand'
  const [compareMode,    setCompareMode]    = useState('any');
  const [selectedBrand,  setSelectedBrand]  = useState(null);    // only when compareMode === 'brand'
  const [selectedRange,  setSelectedRange]  = useState('any');
  const [selectedVariant,setSelectedVariant]= useState('any');
  const [selectedSize,   setSelectedSize]   = useState('any');   // last filter in chain
  const [modeOpen,       setModeOpen]       = useState(false);
  const [rangeOpen,      setRangeOpen]      = useState(false);
  const [variantOpen,    setVariantOpen]    = useState(false);
  const [sizeOpen,       setSizeOpen]       = useState(false);

  const toggleStore = (key) => setExpandedStores(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  // Changing mode resets everything below it and closes all dropdowns
  const handleSetMode = (mode, brand = null) => {
    setCompareMode(mode);
    setSelectedBrand(brand);
    setSelectedRange('any');
    setSelectedVariant('any');
    setSelectedSize('any');
    setExpandedStores(new Set());
    setModeOpen(false);
    setRangeOpen(false);
    setVariantOpen(false);
    setSizeOpen(false);
  };

  // Changing range resets variant + size
  const handleSetRange = (range) => {
    setSelectedRange(range);
    setSelectedVariant('any');
    setSelectedSize('any');
    setExpandedStores(new Set());
    setRangeOpen(false);
    setVariantOpen(false);
    setSizeOpen(false);
  };

  // Changing variant resets size
  const handleSetVariant = (variant) => {
    setSelectedVariant(variant);
    setSelectedSize('any');
    setExpandedStores(new Set());
    setVariantOpen(false);
    setSizeOpen(false);
  };

  // Size is last — only resets expanded accordion state
  const handleSetSize = (size) => {
    setSelectedSize(size);
    setExpandedStores(new Set());
    setSizeOpen(false);
  };

  // ── Mixed-family detection ────────────────────────────────────────────────
  const isMixedFamily = siblings.length > 1 &&
    siblings.some(s => s.ownBrand === true) &&
    siblings.some(s => s.ownBrand === false && s.tier === 'brand');

  const effectiveSiblings = siblings.length > 0
    ? siblings
    : [{ ...product, prices: pricesProp || [] }];

  // ── Base sibling groups (unfiltered by mode) ──────────────────────────────
  const ownBrandSiblings = isMixedFamily
    ? effectiveSiblings.filter(s => s.ownBrand === true)
    : [];
  const brandedSiblings = isMixedFamily
    ? effectiveSiblings.filter(s => s.ownBrand === false && s.tier === 'brand')
    : [];

  // ── Available brands (from branded siblings, for the mode sheet) ──────────
  const availableBrands = [...new Set(brandedSiblings.map(s => s.brand).filter(Boolean))].sort();

  // ── Available ranges (data-driven) ──────────────────────────────────────
  // Non-mixed: derived from all effectiveSiblings.
  // Mixed: derived from the mode-specific sibling group.
  // 'brand' tier value is excluded — it's not a range.
  const availableRanges = (() => {
    if (!isMixedFamily) {
      const tiers = new Set(effectiveSiblings.map(s => s.tier).filter(t => t && t !== 'brand'));
      return TIER_ORDER.filter(t => tiers.has(t));
    }
    let sibs = [];
    if (compareMode === 'own_brand') sibs = ownBrandSiblings;
    else if (compareMode === 'branded') sibs = brandedSiblings;
    else if (compareMode === 'brand')   sibs = brandedSiblings.filter(s => s.brand === selectedBrand);
    const tiers = new Set(sibs.map(s => s.tier).filter(t => t && t !== 'brand'));
    return TIER_ORDER.filter(t => tiers.has(t));
  })();

  // Range selector appears when:
  // - Non-mixed product has 2+ distinct tiers (e.g. Standard + Organic cheddar)
  // - Mixed product in own_brand/branded/brand mode has 2+ distinct tiers
  const showRangeSelector =
    (!isMixedFamily && availableRanges.length > 1) ||
    (isMixedFamily &&
      (compareMode === 'own_brand' || compareMode === 'branded' || compareMode === 'brand') &&
      availableRanges.length > 1);

  // ── Step 1: filter by mode + range ───────────────────────────────────────
  const applyRange = (sibs) =>
    selectedRange === 'any' ? sibs : sibs.filter(s => s.tier === selectedRange);

  const rangeFilteredOwn = (compareMode === 'any' || compareMode === 'own_brand')
    ? applyRange(ownBrandSiblings)
    : [];

  const rangeFilteredBranded =
    compareMode === 'any'     ? applyRange(brandedSiblings) :
    compareMode === 'branded' ? applyRange(brandedSiblings) :
    compareMode === 'brand'   ? applyRange(brandedSiblings.filter(s => s.brand === selectedBrand)) :
    [];

  const rangeFilteredNonMixed = !isMixedFamily ? applyRange(effectiveSiblings) : [];

  // ── Available variants — data-driven from range-filtered set ─────────────
  // Excluded for 'any' mixed mode (mixing own-brand + branded variants is misleading).
  const availableVariants = (() => {
    let sibs = [];
    if (!isMixedFamily)                                        sibs = rangeFilteredNonMixed;
    else if (compareMode === 'own_brand')                      sibs = rangeFilteredOwn;
    else if (compareMode === 'branded' || compareMode === 'brand') sibs = rangeFilteredBranded;
    // 'any' mode → no variant selector shown
    const vs = [...new Set(sibs.map(s => s.variant).filter(v => v && v !== ''))];
    return vs.sort();
  })();

  const showVariantSelector = availableVariants.length > 1;

  // ── Step 2: apply variant filter ─────────────────────────────────────────
  const applyVariant = (sibs) =>
    selectedVariant === 'any' ? sibs : sibs.filter(s => s.variant === selectedVariant);

  // These are variant-filtered (before size) — used to derive availableSizes
  const variantFilteredOwn       = applyVariant(rangeFilteredOwn);
  const variantFilteredBranded   = applyVariant(rangeFilteredBranded);
  const variantFilteredNonMixed  = applyVariant(rangeFilteredNonMixed);

  // ── Available sizes — derived from PRICE ENTRIES, not product docs ───────────
  // Using price-entry sizes handles both data patterns:
  //   • Sizes as separate product docs (e.g. eggs 6/12-pack): each sibling's
  //     retailer docs share the same storeSize → distinct sizes across siblings.
  //   • Sizes as retailer docs under one product doc (e.g. olive oil 250ml/500ml):
  //     one sibling, but its retailer docs each have a distinct p.size value.
  // No size selector shown in mixed 'any' mode (own-brand + branded mixed).
  const availableSizes = (() => {
    let sibs = [];
    if (!isMixedFamily)                                            sibs = variantFilteredNonMixed;
    else if (compareMode === 'own_brand')                          sibs = variantFilteredOwn;
    else if (compareMode === 'branded' || compareMode === 'brand') sibs = variantFilteredBranded;
    // 'any' mixed mode → no size selector
    const sizes = [...new Set(
      sibs.flatMap(s =>
        (s.prices || [])
          .filter(p => p.price > 0)
          .map(p => formatSize(p.size, p.unitType || s.unitType))
          .filter(Boolean)
      )
    )];
    return sizes.sort((a, b) => {
      const numA = parseFloat(a);
      const numB = parseFloat(b);
      if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
      return a.localeCompare(b);
    });
  })();

  const showSizeSelector = availableSizes.length > 1;

  // ── Compute store rows — size filter applied inside computeStoreRows ──────
  // Passing sizeFilter directly means filtering happens at the price-entry level,
  // which works for both product-doc-per-size AND retailer-doc-per-size patterns.
  const sizeArg = selectedSize !== 'any' ? selectedSize : null;

  const ownBrandStoreRows = isMixedFamily ? computeStoreRows(variantFilteredOwn,      sizeArg) : [];
  const brandedStoreRows  = isMixedFamily ? computeStoreRows(variantFilteredBranded,  sizeArg) : [];
  const rawAllStoreRows   = !isMixedFamily ? computeStoreRows(variantFilteredNonMixed, sizeArg) : [];

  // When stores sell different sizes of the same product (e.g. Aldi 500g vs Lidl 1000g),
  // absolute price is misleading. Re-sort by per-unit price for a fair comparison.
  const hasMixedSizesAcrossRows = rawAllStoreRows.length > 1 && (() => {
    const sizes = new Set(rawAllStoreRows.flatMap(r => r.variants.map(v => v.siblingSize).filter(Boolean)));
    return sizes.size > 1;
  })();
  const allStoreRows = (hasMixedSizesAcrossRows && rawAllStoreRows.every(r => r.cheapestUnitPrice))
    ? [...rawAllStoreRows].sort((a, b) => a.cheapestUnitPrice.up - b.cheapestUnitPrice.up)
    : rawAllStoreRows;

  // Group headers only appear in 'any' mode (when both sections are visible)
  const showOwnBrandHeader = isMixedFamily && compareMode === 'any' && ownBrandStoreRows.length > 0;
  const showBrandedHeader  = isMixedFamily && compareMode === 'any' && brandedStoreRows.length > 0;

  // ── Store name helper ─────────────────────────────────────────────────────
  const getStoreName = (storeId) => {
    const normId = (storeId || '').replace(/^store_/, '');
    const store  = stores.find(s => s.id === normId || s.id === storeId);
    if (store) return store.name;
    return normId.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  };

  // ── Mixed 'any' mode summary values ───────────────────────────────────────
  const cheapestOwnRow       = ownBrandStoreRows[0] || null;
  const cheapestBrandedRow   = brandedStoreRows[0]  || null;
  const brandedBrandName     = cheapestBrandedRow?.cheapestEntry?.siblingBrand || 'Branded';
  const cheapestOwnStore     = cheapestOwnRow    ? getStoreName(cheapestOwnRow.storeId)    : null;
  const cheapestBrandedStore = cheapestBrandedRow ? getStoreName(cheapestBrandedRow.storeId) : null;

  // ── Non-mixed Penny values ─────────────────────────────────────────────────
  // ── Single source of truth: cheapest price + which stores share it ───────────
  const minPriceFmt       = allStoreRows.length > 0
    ? Math.min(...allStoreRows.map(r => r.cheapestPrice)).toFixed(2)
    : null;
  const cheapestStoreRows = allStoreRows.filter(r => minPriceFmt && r.cheapestPrice.toFixed(2) === minPriceFmt);
  const allStoresSamePrice = cheapestStoreRows.length === allStoreRows.length;
  const hasCheapestTie    = cheapestStoreRows.length > 1 && !allStoresSamePrice;

  const pennyBest       = allStoreRows[0] || null;
  const pennyWorst      = allStoreRows[allStoreRows.length - 1] || null;
  const pennySavings    = allStoreRows.length > 1 ? pennyWorst.cheapestPrice - pennyBest.cheapestPrice : 0;
  const pennySavingsStr = pennySavings >= 1
    ? `£${pennySavings.toFixed(2)}`
    : `${Math.round(pennySavings * 100)}p`;
  const pennyBestName  = pennyBest  ? getStoreName(pennyBest.storeId)  : null;
  const pennyWorstName = (pennyWorst && pennyWorst.storeId !== pennyBest?.storeId)
    ? getStoreName(pennyWorst.storeId) : null;

  // ── Mixed Penny (non-'any' mode) ───────────────────────────────────────────
  const mixedPennyRow = (() => {
    if (compareMode === 'own_brand') return ownBrandStoreRows[0] || null;
    if (compareMode === 'branded' || compareMode === 'brand') return brandedStoreRows[0] || null;
    return null;
  })();

  // ── Comparison engine ─────────────────────────────────────────────────────
  // One source of truth for best-value classification, badges, and Penny copy.
  // The component does not make any local comparison decisions — it renders
  // whatever the engine returns.
  const allStoresSummary  = buildComparisonSummary(allStoreRows, getStoreName);
  const ownBrandSummary   = buildComparisonSummary(ownBrandStoreRows, getStoreName);
  const brandedSummary    = buildComparisonSummary(brandedStoreRows, getStoreName);

  const mixedPennyText = (() => {
    if (!mixedPennyRow) return null;
    const storeName = getStoreName(mixedPennyRow.storeId);
    if (compareMode === 'own_brand') {
      const rangeStr = selectedRange !== 'any' ? ` ${TIER_LABELS[selectedRange].toLowerCase()}` : '';
      return `For own brand${rangeStr}, ${storeName} is cheapest.`;
    }
    if (compareMode === 'branded') return `For branded, ${storeName} is cheapest.`;
    if (compareMode === 'brand')   return `${storeName} is cheapest for ${selectedBrand}.`;
    return null;
  })();

  // ── Compare mode selector label ────────────────────────────────────────────
  const compareModeLabel =
    compareMode === 'own_brand' ? 'Own brand' :
    compareMode === 'branded'   ? 'Branded'   :
    compareMode === 'brand'     ? selectedBrand :
    'Cheapest overall';

  // ── Active product for header icon ────────────────────────────────────────
  const activeProduct = (() => {
    const rows = isMixedFamily ? [...ownBrandStoreRows, ...brandedStoreRows] : allStoreRows;
    const topEntry = rows[0]?.cheapestEntry;
    if (topEntry) {
      const match =
        effectiveSiblings.find(s => s.id === topEntry.siblingId) ||
        effectiveSiblings.find(s => (s.prices || []).some(p => p.id === topEntry.id));
      if (match) return match;
    }
    return product;
  })();

  const productIconUrl  = activeProduct.iconUrl || getIconOverride(activeProduct);
  const confidenceLabel = getConfidenceLabel(activeProduct);

  // ── Product title size suffix ─────────────────────────────────────────────
  // Derived from ACTUAL price-entry sizes (p.size), not the canonical standardizedSize,
  // so a family like olive oil where all retailer docs live under one product doc
  // (and stores sell different sizes) doesn't incorrectly show a single canonical value.
  //
  // Rule: if every price entry across all active store rows has the same size → show it.
  // If a specific size is selected from the selector → show that.
  const productTitleSuffix = (() => {
    if (selectedSize !== 'any') return selectedSize;
    const activeRows = isMixedFamily
      ? [...ownBrandStoreRows, ...brandedStoreRows]
      : allStoreRows;
    const priceSizes = new Set(
      activeRows.flatMap(row => row.variants.map(v => v.siblingSize).filter(Boolean))
    );
    return priceSizes.size === 1 ? [...priceSizes][0] : null;
  })();

  // ── Add to list — stores preference + uses specific name in toast ────────
  const handleAddToList = () => {
    if (!onAddToBasket) return;

    const preference = {
      type: compareMode,
      ...(compareMode === 'brand' && selectedBrand ? { brandName: selectedBrand } : {}),
      ...(selectedRange   !== 'any' ? { range:   selectedRange   } : {}),
      ...(selectedVariant !== 'any' ? { variant: selectedVariant } : {}),
      ...(selectedSize    !== 'any' ? { size:    selectedSize    } : {}),
    };

    // Use the most relevant rows for the current mode
    const rows = isMixedFamily
      ? ((compareMode === 'own_brand' || compareMode === 'any') && ownBrandStoreRows.length > 0
          ? ownBrandStoreRows
          : brandedStoreRows)
      : allStoreRows;

    const top = rows[0];
    if (!top) return;

    // Use siblingId (stamped onto each price entry by computeStoreRows) for an exact match.
    // Falling back to store-id match risks returning the wrong sibling when multiple tiers
    // are stocked at the same store (e.g. basic + organic both at Sainsbury's).
    const sibling =
      effectiveSiblings.find(s => s.id === top.cheapestEntry.siblingId) ||
      effectiveSiblings.find(s => (s.prices || []).some(p => p.id === top.cheapestEntry.id)) ||
      product;

    // Build a specific toast name — stack qualifiers then append size
    const toastName = (() => {
      const family    = product.name;
      const qualifiers = [];
      if (compareMode === 'brand' && selectedBrand) qualifiers.push(selectedBrand);
      if (selectedRange   !== 'any') qualifiers.push(TIER_LABELS[selectedRange]);
      if (selectedVariant !== 'any') qualifiers.push(formatVariant(selectedVariant));

      let name = qualifiers.length > 0 ? `${qualifiers.join(' ')} ${family}` : family;

      // Prepend brand when available and no qualifier already covers it
      // Brand lives on the retailer doc (cheapestEntry.brand), not the product doc
      if (qualifiers.length === 0) {
        const brand = top.cheapestEntry.brand || sibling.brand;
        if (brand) name = `${brand} ${family}`;
      }

      // Append size as a suffix (e.g. "Organic Free Range Eggs, 6 pack")
      const sizeLabel = selectedSize !== 'any' ? selectedSize : productTitleSuffix;
      if (sizeLabel) name = `${name}, ${sizeLabel}`;

      return name;
    })();

    trackAddToList(product.name, product.category);
    onAddToBasket(sibling, rows.map(r => r.cheapestEntry), preference, toastName);
  };

  // ── Render store rows with accordion ──────────────────────────────────────
  const renderStoreRows = (rows, groupKey, forceExpandable = false, summary = null) => {
    // Build a lookup map from the engine summary so badge decisions come from
    // one place — the comparison engine — not from local component logic.
    const classifiedMap = new Map();
    (summary?.rows || []).forEach(r => classifiedMap.set(r.storeId, r));

    return rows.map((row, i) => {
    const key        = `${groupKey}-${row.storeId}`;
    const isOpen     = expandedStores.has(key);
    const classified = classifiedMap.get(row.storeId);
    const isBestValue = classified?.isBestValue ?? false;
    const badges      = classified?.badges ?? [];
    const canExpand   = forceExpandable || row.hasVariants;

    // Always show size from the cheapest entry so users can verify what they're comparing
    const collapsedSize = row.cheapestEntry?.siblingSize || null;

    return (
      <div key={key} className="pc-store-block">
        <div
          className={`pc-price-row${isBestValue ? ' cheapest' : ''}${canExpand ? ' pc-row-expandable' : ''}`}
          onClick={canExpand ? () => toggleStore(key) : undefined}
          role={canExpand ? 'button' : undefined}
        >
          <div className="pc-row-left">
            <span className="pc-store">{getStoreName(row.storeId)}</span>
            {badges.map(b => (
              <span key={b} className="pc-best-badge pc-best-badge--value">{b}</span>
            ))}
          </div>
          <div className="pc-row-right">
            <div className="pc-price-col">
              <span className="pc-price">£{row.cheapestPrice.toFixed(2)}</span>
              {collapsedSize && (
                <span className="pc-price-size-hint">
                  {collapsedSize}
                  {row.cheapestUnitPrice && ` · ${formatUnitPrice(row.cheapestUnitPrice.up)}/${row.cheapestUnitPrice.label}`}
                </span>
              )}
            </div>
            <span
              className={`pc-expand-chevron${canExpand ? '' : ' pc-expand-chevron--hidden'}`}
              aria-hidden="true"
            >
              {canExpand ? (isOpen ? '▲' : '▼') : ''}
            </span>
          </div>
        </div>

        {isOpen && (
          <div className="pc-variants">
            {row.variants.map((v, vi) => {
              // Build a clear label: store name → sibling name → brand.
              // Always append the size when it's available so the user can
              // tell apart e.g. "Olive Oil 250ml  £1.50" vs "Olive Oil 500ml  £2.80"
              const baseName  = v.productNameStore || v.siblingName || v.siblingBrand || '—';
              const sizeLabel = v.siblingSize && !baseName.toLowerCase().includes(v.siblingSize.toLowerCase())
                ? `, ${v.siblingSize}`
                : '';
              return (
                <div key={v.id || `${key}-v${vi}`} className={`pc-variant-row${isBestValue && vi === 0 ? ' cheapest-variant' : ''}`}>
                  <span className="pc-variant-label">
                    {isBestValue && vi === 0 && <span className="pc-variant-star" aria-hidden="true">⭐</span>}
                    {baseName}{sizeLabel}
                  </span>
                  <span className="pc-variant-price">£{v.price.toFixed(2)}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  });
  };

  // ── Early return — must be after all hooks ────────────────────────────────
  // Use base (unfiltered) sibling data so mode changes can't blank the page
  const baseHasData = isMixedFamily
    ? ownBrandSiblings.some(s => (s.prices || []).some(p => p.price > 0)) ||
      brandedSiblings.some(s => (s.prices || []).some(p => p.price > 0))
    : effectiveSiblings.some(s => (s.prices || []).some(p => p.price > 0));
  if (!product || !baseHasData) return null;

  const allRows    = isMixedFamily ? [...ownBrandStoreRows, ...brandedStoreRows] : allStoreRows;
  const flagStores = allRows.map(r => ({ id: r.storeId, name: getStoreName(r.storeId) }));

  // Whether to render the selector block at all
  const showSelectorBlock = isMixedFamily || showRangeSelector || showVariantSelector || showSizeSelector;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="price-comparison">

      {/* ── Nav row ── */}
      <div className="pc-nav-row">
        {onBack && (
          <button
            className="pc-back-btn"
            onClick={onBack}
            aria-label={aisleContext?.subcategory?.name ? `Back to ${aisleContext.subcategory.name}` : 'Back'}
          >
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="13 18 7 12 13 6"/>
            </svg>
          </button>
        )}
        {aisleContext?.level === 'subcategory' && (
          <nav className="pc-breadcrumb" aria-label="Navigation">
            <button className="pc-crumb-link" onClick={onBackToAisle}>{aisleContext.aisle.name}</button>
            <span className="pc-crumb-sep" aria-hidden="true">›</span>
            <button className="pc-crumb-link" onClick={onBack}>{aisleContext.subcategory.name}</button>
          </nav>
        )}
      </div>

      {/* ── Product header ── */}
      <div className="pc-product-card">
        <div
          className="pc-product-icon"
          style={productIconUrl ? undefined : { background: getCategoryGradient(activeProduct.category) }}
        >
          {productIconUrl
            ? <img src={productIconUrl} alt="" className={`pc-product-photo${isTallIcon(activeProduct) ? ' pc-product-photo--tall' : ''}`} />
            : <span>{getSubcategoryDisplay(activeProduct.subcategory).icon}</span>
          }
        </div>
        <div className="pc-product-text">
          <h2 className="pc-product-name">
            {product.name}
            {productTitleSuffix && (
              <span className="pc-product-size">, {productTitleSuffix}</span>
            )}
          </h2>
        </div>
      </div>

      {/* ── Selector block: Comparing → Range → Type → Size ── */}
      {showSelectorBlock && (
        <div className="pc-mode-selectors">

          {/* Primary: Comparing — only shown for mixed families */}
          {isMixedFamily && (
            <>
              <button
                className="pc-compare-selector"
                onClick={() => { setModeOpen(o => !o); setRangeOpen(false); setSizeOpen(false); setVariantOpen(false); }}
                aria-expanded={modeOpen}
              >
                <span className="pc-compare-selector-label">Comparing</span>
                <span className="pc-compare-selector-value">{compareModeLabel}</span>
                <span className="pc-compare-selector-chevron" aria-hidden="true">{modeOpen ? '▲' : '▼'}</span>
              </button>

              {modeOpen && (
                <div className="pc-mode-options" role="listbox">
                  {[
                    { mode: 'any',       label: 'Cheapest overall' },
                    { mode: 'own_brand', label: 'Own brand'        },
                    { mode: 'branded',   label: 'Branded'          },
                  ].map(({ mode, label }) => {
                    const isActive = compareMode === mode && !selectedBrand;
                    return (
                      <button
                        key={mode}
                        className={`pc-mode-option${isActive ? ' active' : ''}`}
                        onClick={() => handleSetMode(mode)}
                        role="option"
                        aria-selected={isActive}
                      >
                        <span className="pc-mode-option-check" aria-hidden="true">{isActive ? '✓' : ''}</span>
                        {label}
                      </button>
                    );
                  })}
                  {availableBrands.length > 0 && <div className="pc-mode-option-divider" aria-hidden="true" />}
                  {availableBrands.map(brand => {
                    const isActive = compareMode === 'brand' && selectedBrand === brand;
                    return (
                      <button
                        key={brand}
                        className={`pc-mode-option${isActive ? ' active' : ''}`}
                        onClick={() => handleSetMode('brand', brand)}
                        role="option"
                        aria-selected={isActive}
                      >
                        <span className="pc-mode-option-check" aria-hidden="true">{isActive ? '✓' : ''}</span>
                        {brand}
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {/* Secondary: Range (only when multiple tiers exist) */}
          {showRangeSelector && (
            <button
              className="pc-compare-selector"
              onClick={() => { setRangeOpen(o => !o); setModeOpen(false); setVariantOpen(false); setSizeOpen(false); }}
              aria-expanded={rangeOpen}
            >
              <span className="pc-compare-selector-label">Range</span>
              <span className="pc-compare-selector-value">
                {selectedRange === 'any' ? 'Any' : TIER_LABELS[selectedRange]}
              </span>
              <span className="pc-compare-selector-chevron" aria-hidden="true">{rangeOpen ? '▲' : '▼'}</span>
            </button>
          )}

          {rangeOpen && showRangeSelector && (
            <div className="pc-mode-options" role="listbox">
              {[{ tier: 'any', label: 'Any' }, ...availableRanges.map(t => ({ tier: t, label: TIER_LABELS[t] }))].map(({ tier, label }) => {
                const isActive = selectedRange === tier;
                return (
                  <button
                    key={tier}
                    className={`pc-mode-option${isActive ? ' active' : ''}`}
                    onClick={() => handleSetRange(tier)}
                    role="option"
                    aria-selected={isActive}
                  >
                    <span className="pc-mode-option-check" aria-hidden="true">{isActive ? '✓' : ''}</span>
                    {label}
                  </button>
                );
              })}
            </div>
          )}

          {/* Tertiary: Type/variant (only when multiple variants exist) */}
          {showVariantSelector && (
            <button
              className="pc-compare-selector"
              onClick={() => { setVariantOpen(o => !o); setModeOpen(false); setRangeOpen(false); setSizeOpen(false); }}
              aria-expanded={variantOpen}
            >
              <span className="pc-compare-selector-label">Type</span>
              <span className="pc-compare-selector-value">
                {selectedVariant === 'any' ? 'Any' : formatVariant(selectedVariant)}
              </span>
              <span className="pc-compare-selector-chevron" aria-hidden="true">{variantOpen ? '▲' : '▼'}</span>
            </button>
          )}

          {variantOpen && showVariantSelector && (
            <div className="pc-mode-options" role="listbox">
              {[{ v: 'any', label: 'Any' }, ...availableVariants.map(v => ({ v, label: formatVariant(v) }))].map(({ v, label }) => {
                const isActive = selectedVariant === v;
                return (
                  <button
                    key={v}
                    className={`pc-mode-option${isActive ? ' active' : ''}`}
                    onClick={() => handleSetVariant(v)}
                    role="option"
                    aria-selected={isActive}
                  >
                    <span className="pc-mode-option-check" aria-hidden="true">{isActive ? '✓' : ''}</span>
                    {label}
                  </button>
                );
              })}
            </div>
          )}

          {/* Quaternary: Size (last in chain, only when multiple sizes exist) */}
          {showSizeSelector && (
            <button
              className="pc-compare-selector"
              onClick={() => { setSizeOpen(o => !o); setModeOpen(false); setRangeOpen(false); setVariantOpen(false); }}
              aria-expanded={sizeOpen}
            >
              <span className="pc-compare-selector-label">Size</span>
              <span className="pc-compare-selector-value">
                {selectedSize === 'any' ? 'Any' : selectedSize}
              </span>
              <span className="pc-compare-selector-chevron" aria-hidden="true">{sizeOpen ? '▲' : '▼'}</span>
            </button>
          )}

          {sizeOpen && showSizeSelector && (
            <div className="pc-mode-options" role="listbox">
              {[{ s: 'any', label: 'Any' }, ...availableSizes.map(s => ({ s, label: s }))].map(({ s, label }) => {
                const isActive = selectedSize === s;
                return (
                  <button
                    key={s}
                    className={`pc-mode-option${isActive ? ' active' : ''}`}
                    onClick={() => handleSetSize(s)}
                    role="option"
                    aria-selected={isActive}
                  >
                    <span className="pc-mode-option-check" aria-hidden="true">{isActive ? '✓' : ''}</span>
                    {label}
                  </button>
                );
              })}
            </div>
          )}

        </div>
      )}

      {/* ── Summary block ── */}
      {isMixedFamily ? (
        compareMode === 'any' ? (
          /* Default 'any' mode: two-column own brand vs branded summary */
          (cheapestOwnRow || cheapestBrandedRow) && (
            <div className="pc-mixed-header">
              {cheapestOwnRow && (
                <div className="pc-mixed-header-col">
                  <span className="pc-mixed-header-label">Cheapest own brand</span>
                  <span className="pc-mixed-header-store">{cheapestOwnStore}</span>
                  <span className="pc-mixed-header-price">£{cheapestOwnRow.cheapestPrice.toFixed(2)}</span>
                </div>
              )}
              {cheapestOwnRow && cheapestBrandedRow && (
                <div className="pc-mixed-header-divider" aria-hidden="true" />
              )}
              {cheapestBrandedRow && (
                <div className="pc-mixed-header-col">
                  <span className="pc-mixed-header-label">Cheapest branded</span>
                  <span className="pc-mixed-header-store">{brandedBrandName} · {cheapestBrandedStore}</span>
                  <span className="pc-mixed-header-price">£{cheapestBrandedRow.cheapestPrice.toFixed(2)}</span>
                </div>
              )}
            </div>
          )
        ) : (
          /* Filtered mode: Penny card with mode-specific insight */
          mixedPennyRow && mixedPennyText && (
            <div className="pc-penny-card">
              <img src="/Penny Fox open eyes.png" alt="Penny" className="pc-penny-img" />
              <div className="pc-penny-text">
                <span className="pc-penny-label">Penny says</span>
                <p>{mixedPennyText}</p>
              </div>
            </div>
          )
        )
      ) : (
        /* Non-mixed: Penny card (text reflects selected range when set) */
        allStoreRows.length > 0 && (
          <div className="pc-penny-card">
            <img src="/Penny Fox open eyes.png" alt="Penny" className="pc-penny-img" />
            <div className="pc-penny-text">
              <span className="pc-penny-label">Penny says</span>
              {allStoresSummary.pennyMessage ? (
                <p>{allStoresSummary.pennyMessage}</p>
              ) : allStoreRows.length === 1 ? (
                <p>Only available at {pennyBestName}.</p>
              ) : allStoresSamePrice ? (
                <p>Same price across all stores.</p>
              ) : hasCheapestTie ? (
                (() => {
                  const names = cheapestStoreRows.map(r => getStoreName(r.storeId));
                  const nameStr = names.length === 2
                    ? `${names[0]} and ${names[1]}`
                    : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
                  return <p>{nameStr} are tied for cheapest at £{minPriceFmt}.</p>;
                })()
              ) : pennySavings > 0.005 && pennyBestName && pennyWorstName ? (
                selectedRange !== 'any'
                  ? <p>For {TIER_LABELS[selectedRange].toLowerCase()}, {pennyBestName} is {pennySavingsStr} cheaper than {pennyWorstName}.</p>
                  : <p>{pennyBestName} is {pennySavingsStr} cheaper than {pennyWorstName}.</p>
              ) : (
                <p>Same price across all stores.</p>
              )}
            </div>
          </div>
        )
      )}

      {/* ── Price list (store-first with accordion) ── */}
      <div className="pc-price-list">
        {isMixedFamily ? (
          <>
            {ownBrandStoreRows.length > 0 && (
              <>
                {showOwnBrandHeader && <div className="pc-group-header">Own brand</div>}
                {renderStoreRows(ownBrandStoreRows, 'own', false, ownBrandSummary)}
              </>
            )}
            {brandedStoreRows.length > 0 && (
              <>
                {showBrandedHeader && <div className="pc-group-header">Branded</div>}
                {renderStoreRows(brandedStoreRows, 'branded', true, brandedSummary)}
              </>
            )}
          </>
        ) : (
          renderStoreRows(allStoreRows, 'all', false, allStoresSummary)
        )}

      </div>

      {/* ── Request link ── */}
      <div className="pc-missing-request-row">
        <button className="pc-request-missing-btn" onClick={() => setRequestModalOpen(true)}>
          Missing something? Request it →
        </button>
      </div>

      {/* ── Confidence + flag ── */}
      <div className="pc-meta-row">
        <span className="pc-confidence">{confidenceLabel}</span>
        <button className="pc-flag-link" onClick={() => setFlagOpen(true)}>Flag this price</button>
      </div>

      {/* ── Add to list ── */}
      {onAddToBasket && (
        <div className="pc-add-section">
          <button className="pc-add-btn" onClick={handleAddToList}>Add to list</button>
        </div>
      )}

      {/* ── Modals ── */}
      <RequestModal
        isOpen={requestModalOpen}
        onClose={() => setRequestModalOpen(false)}
        onSuccess={() => setShowRequestToast(true)}
        initialProductName={product.name}
        initialProductId={product.productId || product.id}
        showRequestType={true}
      />

      {showRequestToast && (
        <Toast
          message="Requested — we'll check availability."
          onClose={() => setShowRequestToast(false)}
        />
      )}

      <FlagPriceSheet
        isOpen={flagOpen}
        onClose={() => setFlagOpen(false)}
        product={activeProduct}
        stores={flagStores}
        defaultStoreId={allRows[0]?.storeId}
      />

    </div>
  );
}

export default PriceComparison;
